/**
 * Interface for ToC entry from the unified API
 */
export interface ForumTocEntry {
  /** Unique identifier of the ToC entry (use for API calls) */
  id: number;

  /** Forum post ID (reference only) */
  postId: number;

  /** Username of the post author */
  postAuthorUsername: string;

  /** Display name of the post author */
  postAuthorDisplayName?: string | null;

  /** Avatar URL of the post author */
  postAuthorAvatar?: string | null;

  /** ISO 8601 timestamp of when the post was created */
  postCreated: string;

  /** ISO 8601 timestamp of when the post was last updated */
  postUpdated: string;

  /** AI-generated summary of the post content */
  summary: string;

  /** Translations of the summary in different languages */
  translations?: {
    en?: string;
    de?: string;
    fr?: string;
    it?: string;
    es?: string;
    "zh-CN"?: string;
  };

  /** ISO 8601 timestamp of when the ToC entry was created */
  created: string;

  /** ISO 8601 timestamp of when the ToC entry was last updated */
  updated: string;

  /** Username of the last editor (null if never edited) */
  lastEditedBy?: string | null;

  /** ISO 8601 timestamp of when last edited (null if never edited) */
  lastEditedAt?: string | null;

  /** Number of times this entry has been edited */
  editCount: number;

  /** Complete edit history for this entry */
  editHistory: ForumTocEditHistory[];

  /** Expert status tags for the post author (only includes tags relevant to the topic) */
  postAuthorExpertStatus?: string[];
}

/**
 * Interface for the complete Table of Contents data (v2 unified API)
 */
export interface ForumTocData {
  /** Total number of ToC entries */
  count: number;

  /** URL for next page (null if last page) */
  next: string | null;

  /** URL for previous page (null if first page) */
  previous: string | null;

  /** Array of ToC entries for current page */
  results: ForumTocEntry[];

  /** ID of the forum topic */
  topicId: number;

  /** Title of the forum topic */
  topicName: string;

  /** Username of the topic creator */
  topicAuthor: string;

  /** ISO 8601 timestamp of when the topic was created */
  topicCreated: string;

  /** ISO 8601 timestamp of when the ToC was generated */
  generatedAt: string;

  /** Supported languages for translations */
  supportedLanguages: string[];
}

/**
 * Interface for the API response when ToC is ready (200 OK)
 */
export interface ForumTocSuccessResponse extends ForumTocData {
  // Inherits all properties from ForumTocData
}

/**
 * Interface for the API response when ToC is being built (202 Accepted)
 */
export interface ForumTocBuildingResponse {
  /** Status indicator */
  status: "building";

  /** User-friendly message about the building state */
  message: string;
}

/**
 * Union type for all possible ToC API responses
 */
export type ForumTocResponse = ForumTocSuccessResponse | ForumTocBuildingResponse;

/**
 * Type guard to check if response is a success response
 */
export function isForumTocSuccessResponse(response: any): response is ForumTocSuccessResponse {
  return (
    response &&
    typeof response === "object" &&
    "topicId" in response &&
    "topicName" in response &&
    "results" in response &&
    Array.isArray(response.results)
  );
}

/**
 * Type guard to check if response is a building response
 */
export function isForumTocBuildingResponse(response: any): response is ForumTocBuildingResponse {
  return (
    response &&
    typeof response === "object" &&
    "status" in response &&
    response.status === "building" &&
    "message" in response
  );
}

/**
 * Interface for ToC entry edit history
 */
export interface ForumTocEditHistory {
  /** Unique identifier of the edit */
  id: number;

  /** Username of the editor (null for AI edits) */
  editorUsername: string | null;

  /** Display name of the editor */
  editorDisplayName?: string;

  /** Avatar URL of the editor (null for AI edits) */
  editorAvatar?: string | null;

  /** Whether this edit was made by AI */
  isAiEdit: boolean;

  /** Previous summary text */
  oldText: string;

  /** New summary text */
  newText: string;

  /** Optional reason for the edit */
  editReason?: string;

  /** ISO 8601 timestamp of when the edit was made */
  created: string;
}

/**
 * Interface for editing a ToC entry
 */
export interface ForumTocEditRequest {
  /** Updated summary text (10-500 characters) */
  summary: string;

  /** Optional reason for the edit */
  editReason?: string;
}
